import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { toDateTimeLocal } from '@/components/workorders/WorkOrderCreateModal';
import {
  defaultCourtesyLoanRange,
  isCourtesyVehicleUnavailableError,
  useCourtesyAvailability,
  useReserveCourtesyLoan,
} from '@/hooks/useCourtesyVehicles';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';

export interface CourtesyWorkOrderRef {
  id: string;
  scheduledAt: string;
  estimatedDurationHours: number | null;
}

interface CourtesyReserveDialogProps {
  open: boolean;
  workOrder: CourtesyWorkOrderRef;
  onClose: () => void;
}

function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function CourtesyReserveDialog({
  open,
  workOrder,
  onClose,
}: CourtesyReserveDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const reserveMutation = useReserveCourtesyLoan();

  const defaults = useMemo(
    () =>
      defaultCourtesyLoanRange({
        scheduledAt: workOrder.scheduledAt,
        estimatedDurationHours: workOrder.estimatedDurationHours,
      }),
    [workOrder.estimatedDurationHours, workOrder.scheduledAt],
  );

  const [plannedStartLocal, setPlannedStartLocal] = useState('');
  const [plannedEndLocal, setPlannedEndLocal] = useState('');
  const [courtesyVehicleId, setCourtesyVehicleId] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlannedStartLocal(toDateTimeLocal(new Date(defaults.plannedStart)));
    setPlannedEndLocal(toDateTimeLocal(new Date(defaults.plannedEnd)));
    setCourtesyVehicleId('');
    setIsDirty(false);
  }, [defaults.plannedEnd, defaults.plannedStart, open]);

  const plannedStartIso = fromDateTimeLocal(plannedStartLocal);
  const plannedEndIso = fromDateTimeLocal(plannedEndLocal);
  const rangeValid =
    !!plannedStartIso &&
    !!plannedEndIso &&
    new Date(plannedEndIso).getTime() > new Date(plannedStartIso).getTime();

  const { data: availability, isLoading: availabilityLoading } = useCourtesyAvailability(
    rangeValid ? plannedStartIso : undefined,
    rangeValid ? plannedEndIso : undefined,
  );

  const freeCars = (availability ?? []).filter((car) => car.isActive && car.available);

  const handleSubmit = async () => {
    if (!plannedStartIso || !plannedEndIso || !courtesyVehicleId) return;
    try {
      await reserveMutation.mutateAsync({
        courtesyVehicleId,
        plannedStart: plannedStartIso,
        plannedEnd: plannedEndIso,
        bookingId: workOrder.id,
      });
      const plate =
        freeCars.find((car) => car.id === courtesyVehicleId)?.registrationNumber ?? courtesyVehicleId;
      notify.success(t('courtesy.reserved'), t('courtesy.reservedDesc', { plate }));
      onClose();
    } catch (error) {
      notify.error(
        tc('error'),
        isCourtesyVehicleUnavailableError(error)
          ? t('courtesy.unavailable')
          : describeApiError(error, tc, { fallback: t('courtesy.saveFailed') }),
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('courtesy.reserveTitle')}
      isDirty={isDirty}
      onSubmit={() => { void handleSubmit(); }}
      isLoading={reserveMutation.isPending}
      submitDisabled={!rangeValid || !courtesyVehicleId || reserveMutation.isPending}
      submitLabel={t('courtesy.reserve')}
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('courtesy.plannedStart')}
            type="datetime-local"
            value={plannedStartLocal}
            onChange={(event) => {
              setPlannedStartLocal(event.target.value);
              setCourtesyVehicleId('');
              setIsDirty(true);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('courtesy.plannedEnd')}
            type="datetime-local"
            value={plannedEndLocal}
            onChange={(event) => {
              setPlannedEndLocal(event.target.value);
              setCourtesyVehicleId('');
              setIsDirty(true);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {rangeValid && !availabilityLoading && freeCars.length === 0 && (
            <Alert severity="info">{t('courtesy.noFreeCars')}</Alert>
          )}
          <FormControl fullWidth disabled={!rangeValid || availabilityLoading || freeCars.length === 0}>
            <InputLabel id="courtesy-vehicle-select-label">{t('courtesy.vehicle')}</InputLabel>
            <Select
              labelId="courtesy-vehicle-select-label"
              label={t('courtesy.vehicle')}
              value={courtesyVehicleId}
              onChange={(event) => {
                setCourtesyVehicleId(String(event.target.value));
                setIsDirty(true);
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                {t('courtesy.selectVehicle')}
              </MenuItem>
              {freeCars.map((car) => (
                <MenuItem key={car.id} value={car.id}>
                  {car.displayName
                    ? `${car.registrationNumber} · ${car.displayName}`
                    : car.registrationNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
